import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketApi } from './api';
import type { TicketCategory, TicketPriority } from './api';
import { TICKET_CONTEXT_KEY } from '../../utils/assetFailureEscalation';
import type { AssetFailureTicketContext } from '../../utils/assetFailureEscalation';
import {
  Ticket, AlertTriangle, CheckCircle2, X, Send, ArrowRight,
  Mail, Tag, FileText, Zap, Package, Hash, ChevronDown,
} from 'lucide-react';

const PRIORITIES: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const PRIORITY_STYLES: Record<TicketPriority, string> = {
  LOW: 'bg-gradient-to-r from-slate-100 to-gray-100 text-gray-700 ring-1 ring-gray-300',
  MEDIUM: 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-300',
  HIGH: 'bg-gradient-to-r from-amber-100 to-orange-100 text-orange-700 ring-1 ring-orange-300',
  CRITICAL: 'bg-gradient-to-r from-rose-100 to-red-100 text-red-700 ring-1 ring-red-300',
};

const emptyForm = {
  categoryId: '',
  subject: '',
  description: '',
  raisedBy: '',
  priority: 'MEDIUM' as TicketPriority,
  assetId: '',
  productOrCampaign: '',
};

function getLoggedInEmail() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.email || '';
  } catch {
    return '';
  }
}

export default function RaiseTicket() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [form, setForm] = useState({ ...emptyForm, raisedBy: getLoggedInEmail() });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; reference: string; resolutionDueAt: string } | null>(null);
  const [assetFailure, setAssetFailure] = useState<AssetFailureTicketContext | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(TICKET_CONTEXT_KEY) || 'null');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    ticketApi.getCategories().then((loadedCategories) => {
      setCategories(loadedCategories);
      if (assetFailure) {
        const validAssetId = assetFailure.assetId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(assetFailure.assetId)
          ? assetFailure.assetId
          : '';
        const ingestionCategory = loadedCategories.find(category => category.code === 'INGESTION_FAILURE');
        setForm(current => ({
          ...current,
          categoryId: ingestionCategory ? String(ingestionCategory.id) : current.categoryId,
          subject: `Asset upload failed after 3 attempts: ${assetFailure.fileName}`,
          description: `${assetFailure.source}\n\nAsset: ${assetFailure.fileName}\n${assetFailure.trackingId ? `Upload tracking ID: ${assetFailure.trackingId}\n` : ''}${validAssetId ? `Asset UUID: ${validAssetId}\n` : 'Asset UUID: Not created because ingestion did not complete\n'}Failure: ${assetFailure.error}\nAttempts: ${assetFailure.attempts}`,
          priority: 'HIGH',
          assetId: validAssetId
        }));
      }
    }).catch((err: Error) => setError(err.message));
  }, []);

  const selectedCategory = categories.find((category) => String(category.id) === String(form.categoryId));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const ticket = await ticketApi.raiseTicket({ ...form, categoryId: Number(form.categoryId) });
      setCreated(ticket);
      sessionStorage.removeItem(TICKET_CONTEXT_KEY);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to raise ticket');
    } finally {
      setSubmitting(false);
    }
  }

  /* ──────────────── SUCCESS STATE ──────────────── */
  if (created) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white shadow-md">
              <CheckCircle2 size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Ticket raised successfully</h2>
              <p className="text-sm text-emerald-700/70">
                Your support request has been logged.
              </p>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">
                Reference Number
              </div>
              <div className="text-xl font-bold text-gray-900 font-mono">
                {created.reference}
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              We aim to respond within <strong className="text-gray-900">48 hours</strong> and resolve within{' '}
              <strong className="text-gray-900">5 days</strong>. Resolution is targeted by{' '}
              <strong className="text-gray-900">
                {new Date(created.resolutionDueAt).toLocaleString()}
              </strong>.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={() => navigate(`/tickets/view/${created.id}`)}
                className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold
                  bg-gradient-to-r from-blue-600 to-indigo-600 text-white
                  border border-blue-700 shadow-sm
                  hover:from-blue-700 hover:to-indigo-700 hover:shadow-md
                  active:scale-[0.98]
                  transition-all duration-200"
              >
                <span>View ticket</span>
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                onClick={() => {
                  setCreated(null);
                  setForm({ ...emptyForm, raisedBy: getLoggedInEmail() || form.raisedBy });
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium
                  text-gray-700 border border-gray-300 hover:bg-gray-50
                  active:scale-[0.98]
                  transition-all duration-200"
              >
                Raise another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ──────────────── FORM STATE ──────────────── */
  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-purple-100 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shadow-md">
            <Ticket size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Raise a support ticket</h2>
            <p className="text-sm text-purple-700/70">
              Report ingestion failures, corrupt content, or system issues.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          {/* Asset failure alert */}
          {assetFailure && (
            <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0 mt-0.5">
                <AlertTriangle size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-900">
                  Asset upload failed three times
                </div>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  Review the prefilled details below and raise a support ticket.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.removeItem(TICKET_CONTEXT_KEY);
                  setAssetFailure(null);
                }}
                className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-amber-600 hover:bg-amber-100 transition"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-gradient-to-r from-rose-50 to-red-50">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center text-red-600 shrink-0 mt-0.5">
                <AlertTriangle size={16} />
              </div>
              <p className="text-sm text-red-700 leading-relaxed">{error}</p>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
              <Tag size={12} className="text-gray-400" />
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={form.categoryId}
              onChange={(event) => setForm({ ...form, categoryId: event.target.value })}
              required
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
            >
              <option value="">Select a category...</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            {selectedCategory && (
              <p className="text-xs text-gray-500 mt-1.5 pl-1 leading-relaxed">
                {selectedCategory.description}
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
              <FileText size={12} className="text-gray-400" />
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              value={form.subject}
              onChange={(event) => setForm({ ...form, subject: event.target.value })}
              maxLength={200}
              required
              placeholder="Short summary"
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
            />
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
              <FileText size={12} className="text-gray-400" />
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              rows={5}
              required
              placeholder="What happened, affected assets, steps to reproduce..."
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition resize-y"
            />
          </div>

          {/* Email + Priority */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                <Mail size={12} className="text-gray-400" />
                Your email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={form.raisedBy}
                onChange={(event) => setForm({ ...form, raisedBy: event.target.value })}
                required
                placeholder="you@company.com"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                <Zap size={12} className="text-gray-400" />
                Priority
              </label>
              <select
                value={form.priority}
                onChange={(event) => setForm({ ...form, priority: event.target.value as TicketPriority })}
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
              <span
                className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_STYLES[form.priority]}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {form.priority}
              </span>
            </div>
          </div>

          {/* Asset ID + Product/Campaign */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                <Hash size={12} className="text-gray-400" />
                Asset ID
              </label>
              <input
                value={form.assetId}
                onChange={(event) => setForm({ ...form, assetId: event.target.value })}
                placeholder="e.g. AST-44021"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5">
                <Package size={12} className="text-gray-400" />
                Product / Campaign
              </label>
              <input
                value={form.productOrCampaign}
                onChange={(event) => setForm({ ...form, productOrCampaign: event.target.value })}
                placeholder="for allocation requests"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-2 border-t border-gray-100">
            <button
              type="submit"
              disabled={submitting}
              className={`group inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm transition-all duration-200 active:scale-[0.98] ${submitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 hover:shadow-md'
                }`}
            >
              <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
              <span>{submitting ? 'Submitting...' : 'Submit ticket'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
